import { Component, inject } from '@angular/core';
import {
    DxButtonComponent,
    DxDataGridComponent,
    DxFormComponent,
    DxTemplateDirective,
} from 'devextreme-angular';
import {
    DxiColumnComponent,
    DxiItemComponent,
    DxoColumnChooserComponent,
    DxoExportComponent,
    DxoFilterRowComponent,
    DxoHeaderFilterComponent,
    DxoLoadPanelComponent,
    DxoPagerComponent,
    DxoPagingComponent,
    DxoPositionComponent,
    DxoScrollingComponent,
    DxoSearchComponent,
    DxoSearchPanelComponent,
    DxoSelectionComponent,
    DxoSortingComponent,
    DxoToolbarComponent,
} from 'devextreme-angular/ui/nested';
import { FilterPanelComponent } from '../../../components/library/filter-panel/filter-panel.component';
import {
    FilterConfig,
    GridPageComponent,
} from '../../common/grid-page.component';
import {
    AccessType,
    AppointmentGridViewDto,
    AppointmentLoadType,
    AppointmentStatus,
    AuthService,
    PermissionType,
    ShuntLocationType,
    TrailerType,
} from '@services';
import EnumUtils from '../../../shared/enum-utils';
import { CommonModule, NgClass } from '@angular/common';
import { DxDataGridTypes } from 'devextreme-angular/ui/data-grid';
import { Router, RouterLink } from '@angular/router';
import { DATE_TIME_FORMAT, YES_NO_OPTIONS } from 'src/app/shared/constants';
import { AppointmentStatusPipe } from 'src/app/pipes/appointment-status.pipe';
import { CapitalCasePipe } from 'src/app/pipes/capital-case.pipe';
import { TrailerTypePipe } from 'src/app/pipes/trailer-type.pipe';
import { AppointmentLoadTypePipe } from 'src/app/pipes/appointment-load-type-status.pipe';

@Component({
    selector: 'app-appointments',
    imports: [
        CommonModule,
        CapitalCasePipe,
        DxDataGridComponent,
        DxoScrollingComponent,
        DxoPagingComponent,
        DxoPagerComponent,
        DxoLoadPanelComponent,
        DxoPositionComponent,
        DxoSortingComponent,
        DxoSearchComponent,
        DxoHeaderFilterComponent,
        DxoFilterRowComponent,
        DxoColumnChooserComponent,
        DxoSearchPanelComponent,
        DxoSelectionComponent,
        DxoExportComponent,
        DxoToolbarComponent,
        DxiItemComponent,
        DxButtonComponent,
        DxiColumnComponent,
        DxTemplateDirective,
        NgClass,
        FilterPanelComponent,
        DxFormComponent,
        AppointmentStatusPipe,
        AppointmentLoadTypePipe,
        TrailerTypePipe,
        RouterLink,
    ],
    providers: [AppointmentStatusPipe, TrailerTypePipe, AppointmentLoadTypePipe],
    templateUrl: './appointments.component.html',
})
export class AppointmentsComponent extends GridPageComponent<AppointmentGridViewDto> {
    private router = inject(Router);
    private appointmentStatusPipe = inject(AppointmentStatusPipe);
    private appointmentLoadTypePipe = inject(AppointmentLoadTypePipe);
    private trailerTypePipe = inject(TrailerTypePipe);
    private authService = inject(AuthService);

    protected override exportName: string = 'Appointments Report';
    protected offset: number = 100;
    public dateTimeFormat = DATE_TIME_FORMAT;

    dataSource = this.dataSources.appointments();

    shuntLocationOptions = EnumUtils.getEnumOptions(ShuntLocationType);

    loadTypeOptions = EnumUtils.getEnumOptions(AppointmentLoadType, (v) =>
        this.appointmentLoadTypePipe.transform(v),
    );

    statusOptions = EnumUtils.getEnumOptions(AppointmentStatus, (v) =>
        this.appointmentStatusPipe.transform(v),
    );

    trailerTypeOptions = EnumUtils.getEnumOptions(TrailerType, (v) =>
        this.trailerTypePipe.transform(v),
    );

    public yesNoOptions = YES_NO_OPTIONS;

    protected override filterConfig: FilterConfig[] = [
        { field: 'number', op: 'contains' },
        { field: 'supplier', op: 'contains' },
        { field: 'carrier', op: 'contains' },
        { field: 'shuntLocation', op: '=' },
        { field: 'shuntLocationNumber', op: 'contains' },
        { field: 'status', op: '=' },
        { field: 'requestDate', op: '>=' },
        { field: 'confirmedDate', op: '>=' },
        { field: 'billOfLadingNumber', op: 'contains' },
        { field: 'poOrStoNumber', op: 'contains' },
        { field: 'palletCount', op: '=' },
        { field: 'loadType', op: '=' },
        { field: 'trailerNumber', op: 'contains' },
        { field: 'weightOfShipment', op: 'contains' },
        { field: 'correctBillOfLadingNumber', op: 'contains' },
        { field: 'correctTrailerNumber', op: 'contains' },
        { field: 'trailerType', op: '=' },
        { field: 'trailerTemperature', op: 'contains' },
        { field: 'driverName', op: 'contains' },
        { field: 'driverId', op: 'contains' },
        { field: 'sealNumber', op: 'contains' },
        { field: 'sealMatchesPaperwork', op: '=' },
        { field: 'score', op: '=' },
        { field: 'percentage', op: '=' },
    ];

    constructor() {
        super();
        this.init(AppointmentGridViewDto);
    }

    protected getBadgeClass(status: AppointmentStatus): string {
        return EnumUtils.getAppointmentStatusBadgeClass(status);
    }

    addAppointment = async () => {
        await this.router.navigate(['/inbound/appointments/create']);
    };

    async onRowDblClick(e: DxDataGridTypes.RowDblClickEvent) {
        await this.router.navigate([
            `/inbound/appointments/details/${e.data.number}`,
        ]);
    }

    get canCreate(){
        return this.authService.checkPermission(PermissionType.Appointments, AccessType.Edit);
    }
}
